import {
  Component,
  ViewEncapsulation,
  inject,
  HostListener,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { fadeInGlideDown } from '@shared/utils/animations';
import { SystemService } from '@shared/service/system/system.service';
import { AuthService } from '../../auth/service/auth.service';
import { SelectTypes } from '@shared/model/enums/select-types.enum';
import { CommonModule } from '@angular/common';
import { LogoComponent } from '@shared/svg/logo/logo.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { IconModule } from '@coreui/icons-angular';
import { ENVIRONMENT } from '@env/environment';
import { ButtonDirective, NavLinkDirective, RowComponent, ColComponent } from '@coreui/angular-pro';
import { RouterModule } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import { NgSelectModule, NgSelectComponent } from '@ng-select/ng-select';
import { Permissions } from '../../auth/model/interfaces/permissions.interface';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-top-panel',
  templateUrl: './top-panel.component.html',
  styleUrls: ['./top-panel.component.scss'],
  animations: [fadeInGlideDown()],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    LogoComponent,
    TranslateModule,
    FormsModule,
    RouterModule,
    ButtonDirective,
    NavLinkDirective,
    NgSelectModule,
    RowComponent,
    ColComponent,
  ],
  providers: [IconModule],
})
export class TopPanelComponent implements AfterViewInit, OnDestroy {
  protected systemService = inject(SystemService);
  protected authService = inject(AuthService);

  @ViewChild('companySelect') companySelect?: NgSelectComponent;
  @ViewChild('projectSelect') projectSelect?: NgSelectComponent;

  iconsStyle = ENVIRONMENT.FONTAWESOME.style;

  protected readonly SelectTypes = SelectTypes;

  readonly icons = {
    hamburgerMenu: 'fa-bars',
    astronaut: 'fa-user-astronaut',
    user: 'fa-user',
    powerOff: 'fa-power-off',
  };

  animationDuration = {
    value: '',
    params: { duration: '500ms' },
  };

  disabled = false; // Disabled when dashboard modals are open
  private topPanelDisabledSubscription?: Subscription;
  userMenuExpanded = false; // Track user menu open/close state

  constructor() {
    // CRITICAL: Subscribe to sidebar disabled state (when dashboard modals are open)
    // This prevents interaction with top panel while modals are active
    this.topPanelDisabledSubscription = this.systemService.sidebarDisabled$.subscribe((disabled) => {
      this.disabled = disabled;
    });

    // Close user menu when clicking outside
    document.addEventListener('click', (event) => {
      const userMenu = document.getElementById('unified-user-menu');
      if (userMenu && !userMenu.contains(event.target as Node)) {
        this.userMenuExpanded = false;
      }
    });
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuExpanded = !this.userMenuExpanded;
  }

  toggleSidebar(): void {
    this.systemService.toggleSidebar();
  }

  openAgentScreen() {
    const url = '/' + this.systemService.getSelectedProject() + '/rep';
    window.open(url, '_blank');
  }

  get isSidebarExpanded(): boolean {
    return this.systemService.isSidebarExpanded;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSidebar();
    }
  }

  async onLogoutClick(): Promise<void> {
    await this.authService.doLogout();
  }

  loadUserProfile = async (): Promise<void> => {
    this.authService.navigateTo(`users/${this.authService.user?.uuid}`);
  };

  get systemVersion(): string {
    return this.systemService?.systemSettings?.VERSION?.version || '*';
  }

  onCompanyChange(selectedValue: { label: string; value: string }): void {
    if (selectedValue.value !== this.systemService.COMPANY_DEFAULT_SELECTION) {
      this.onProjectChange(selectedValue.value);
    }
  }

  onProjectChange(selectedValue: string): void {
    if (selectedValue) {
      // Navigate to /app/ and reload the page for user-initiated project changes
      window.location.href = '/app/';
    }
  }

  searchAllFn = () => true;

  get showCompanySelect(): boolean {
    return !!this.authService.user?.isSuperuser;
  }

  get hasAgentScreenPermission(): boolean {
    const permissions = this.authService.getCurrentProjectUserPermissions() as unknown as Permissions;
    return !!permissions?.hasProjectAgentScreenUse;
  }

  /**
   * Setup message listener after view is initialized
   */
  ngAfterViewInit(): void {
    // CRITICAL: Listen for messages from iframe to close ng-select dropdowns
    // When user clicks inside iframe, it sends 'close-parent-dropdowns' message
    // This is necessary because clicks inside iframe don't bubble up to parent document
    window.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'close-parent-dropdowns') {
        this.closeAllDropdowns();
      }
    });
  }

  ngOnDestroy(): void {
    this.topPanelDisabledSubscription?.unsubscribe();
  }

  /**
   * Close all dropdowns (ng-select and custom user menu)
   */
  private closeAllDropdowns(): void {
    this.companySelect?.close();
    this.projectSelect?.close();
    // Close custom user menu
    this.userMenuExpanded = false;
  }

  /**
   * Close iframe modals when top panel is clicked
   * This ensures that modals in the iframe (old app) close when user clicks
   * anywhere on the top panel (parent window)
   */
  @HostListener('click')
  onTopPanelClick(): void {
    const iframe = document.getElementById('oldAppIframe') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'close-modals' }, '*');
    }
  }
}
